import { useAccount } from '@src/contexts';
import {
  useGetBusinessIndustries,
  useGetBusinessTypesByCountry
} from '@src/hooks/queries/application';
import { getBusinessInformationSchema } from '../utils';
import Api from '@sendsprint/api-types';
import { BusinessInformationFormData } from '../components/businessInformationStep';
import { useMemo } from 'react';
import { v4 } from 'uuid';
import ClientApi from '@src/types/client';
import { checkNAValues } from '@src/utils/checkNAValues';

interface IUseBusinessInformationFormOptions {
  businessInformationDetails:
    | ClientApi.Onboarding.GetBusinessInformationDetails.Response['data']
    | undefined;
}

const useBusinessInformationForm = ({
  businessInformationDetails
}: IUseBusinessInformationFormOptions) => {
  const { userAddress, user, isLoggedIn } = useAccount();
  const countryValue = userAddress?.country;

  const { data: businessIndustries = [], isLoading: businessIndustriesLoading } =
    useGetBusinessIndustries({ enabled: isLoggedIn });
  const { data: businessTypes = [], isLoading: businessTypesLoading } =
    useGetBusinessTypesByCountry(
      { countryCode: countryValue || '' },
      { enabled: !!countryValue && isLoggedIn }
    );

  const validationSchema = getBusinessInformationSchema({
    businessCountry: userAddress?.country as Api.Model.CountryInitials
  });

  const businessTypesWithStringIds = businessTypes.map((type) => ({
    ...type,
    id: String(type.id)
  }));
  const businessIndustriesWithStringIds = businessIndustries.map((industry) => ({
    ...industry,
    id: String(industry.id)
  }));

  const initialValues = useMemo<BusinessInformationFormData>(() => {
    const businessInfoDetails = businessInformationDetails?.businessInfomation;

    const typeOfBusiness = businessTypes
      ?.find((type) => type?.name === businessInfoDetails?.businessType)
      ?.id?.toString();
    const businessIndustry = businessIndustries
      .find((industry) => industry.name === businessInfoDetails?.businessIndustry)
      ?.id.toString();
    const defaultAddress = [
      {
        id: v4(),
        address: '',
        city: '',
        state: '',
        zipCode: '',
        registeredAddressEvidence: null,
        show: true
      }
    ];
    const mappedAddress = businessInfoDetails?.address?.map(
      (address: ClientApi.Onboarding.GetBusinessInformationDetails.AddressArray) => ({
        id: v4(),
        address: address?.address || '',
        city: address?.city || '',
        state: String(address?.stateId),
        zipCode: address?.zipCode || '',
        registeredAddressEvidence: address.registeredAddressEvidence,
        show: true
      })
    );

    return {
      typeOfBusiness: checkNAValues(typeOfBusiness || ''),
      businessDescription: checkNAValues(businessInfoDetails?.about || ''),
      businessEmailAddress: checkNAValues(businessInfoDetails?.emailAddress || ''),
      businessIndustry: checkNAValues(businessIndustry || ''),
      businessName: checkNAValues(businessInfoDetails?.name || ''),
      businessPhoneNumber: checkNAValues(businessInfoDetails?.phoneNumber || ''),
      logo: checkNAValues(businessInfoDetails?.logoUrl || null),
      tradeName: checkNAValues(businessInfoDetails?.tradeName || ''),
      companyWebsiteUrl: checkNAValues(businessInfoDetails?.websiteUrl || ''),
      companyRegistrationNumber: checkNAValues(
        businessInfoDetails?.companyRegistrationNumber || ''
      ),
      taxIdentificationNumber: checkNAValues(businessInfoDetails?.taxIdentificationNumber || ''),
      einNumber: checkNAValues(businessInfoDetails?.einNumber || ''),
      transactionValues: checkNAValues(
        businessInfoDetails?.estimatedTransactionValueInUsd?.toString() || ''
      ),
      pciDssLevel: checkNAValues(businessInfoDetails?.pciDssLevel?.toString() || ''),
      fraudToolsInPlace: checkNAValues(businessInfoDetails?.fraudToolInPlace || ''),
      useRegisteredAddress: false,
      addressInfo: checkNAValues(mappedAddress?.length ? mappedAddress : defaultAddress)
    };
  }, [businessInformationDetails, businessTypes, businessIndustries]);

  return {
    initialValues,
    businessIndustriesWithStringIds,
    businessTypesWithStringIds,
    validationSchema,
    businessTypesLoading,
    user,
    businessIndustriesLoading,
    userAddress,
    countryValue
  };
};

export default useBusinessInformationForm;
